/**
 * The Thursday email — docs/BUSINESS-RULES.md.
 *
 * The crews are paid on Thursday afternoon, out of the run in `payout.ts`. That
 * run only contains work somebody ticked: a work order nobody marked complete
 * is not in it, is not late, and does not appear anywhere as a problem — the
 * crew simply is not paid, and stays unpaid every week until they ring up.
 *
 * So on Thursday morning, before the money goes, the four people who can do
 * something about it are sent every open job and told which ones are waiting on
 * a tick. Jonathon, 2026-09-20: it "reminds them to follow the process in
 * roofboard (lysa marks the work order complete, lysa marks it approved, kelsey
 * pays them via mercury and logs it in quickbooks)".
 *
 * Three things are decided here rather than in the Edge Function that sends it:
 *
 *  1. **Which jobs are open.** The three sold stages before `closed` — signed,
 *     in production, complete — which are exactly the three groups he asked the
 *     email to name. A closed or lost job is nobody's Thursday problem.
 *  2. **What "needs attention" means.** Roof Board cannot see a roof. It knows
 *     only what has been ticked, so every reason below is the absence of a tick
 *     next to something that says the work happened. `orderReason` is the whole
 *     rule and it gives each work order **one** reason — the next thing to do
 *     about it — because a line with three warnings on it tells a reader to do
 *     nothing three times.
 *  3. **Who owns the line.** Grouped by the job's rep, so Samuel reads his and
 *     Lysa reads hers. A job with no rep is nobody's by name, so it goes in its
 *     own group at the end rather than being hidden.
 *
 * In-house crews never appear. They are on payroll, not paid per job, and
 * `isPayable` in payout.ts is the same rule the payout board uses.
 */

import { addCents, formatMoney, type Cents } from "./money.js";
import {
  isPayable,
  payoutState,
  payoutTotal,
  type PayoutOrder,
  type PayoutState,
} from "./payout.js";
import { crewLabel } from "./crewName.js";
import type { JobStage } from "./types.js";

/** The stages that are somebody's problem on a Thursday morning. */
export const REMINDER_STAGES = ["signed", "in_production", "complete"] as const;

export type ReminderStage = (typeof REMINDER_STAGES)[number];

/**
 * What each group is called in the email.
 *
 * His words, not the stage names: he asked for "signed jobs,
 * scheduled/ongoing jobs, completed jobs".
 */
export const REMINDER_STAGE_TITLE: Record<ReminderStage, string> = {
  signed: "Signed",
  in_production: "Scheduled / ongoing",
  complete: "Completed",
};

/** A job with no rep on it still has to be read by somebody. */
export const NO_REP = "No rep on the job";

export type ReminderReason =
  /** Ticked complete. Lysa has not approved it, so Kelsey cannot pay it. */
  | "awaiting_approval"
  /** Approved. The money has not gone out and nobody has said it did. */
  | "awaiting_payment"
  /** The day the crew was booked for has been and gone, with no tick. */
  | "booked_day_passed"
  /** The job itself reads Complete, with no tick on the crew's order. */
  | "job_complete_not_ticked";

/** The sentence the email prints for each reason. */
export const REMINDER_REASON_TEXT: Record<ReminderReason, string> = {
  awaiting_approval: "complete — waiting on Lysa to approve it",
  awaiting_payment: "approved — waiting on Kelsey to pay it",
  booked_day_passed:
    "booked day has passed — Lysa to mark the work order complete",
  job_complete_not_ticked:
    "job reads Complete — Lysa to mark the work order complete",
};

/**
 * What the email says beside a payout on hold: "on hold: no W-9".
 *
 * Jonathon, 2026-10-27 (ADR 0315): a held payout is listed as on hold with its
 * reason, so the email still says a crew is owed and why it has not been paid.
 * It is not a reason to chase — nobody can tick their way past a missing W-9 —
 * so it never flags the job and never joins the run to pay today.
 */
export function holdText(note: string | null): string {
  return note ? `on hold: ${note}` : "on hold";
}

export type ReminderOrder = PayoutOrder & {
  id: string;
  order_number: number;
  /** When the crew was booked to be there. */
  start_at: string | null;
  /** When the booking ends. A multi-day job is late only after its last day. */
  end_at?: string | null;
  /**
   * False when the order belongs to an estimate that is no longer the job's
   * live one. Set by the database (`set_document_is_live`), never by us.
   */
  is_live?: boolean | null;
  /** Why it was sent back or put on hold. The hold's reason, when held. */
  payout_note?: string | null;
};

export interface ReminderJob {
  id: string;
  job_number: number | null;
  /** The customer, so a reader knows the job by who it is for. */
  customer_name?: string | null;
  address: string | null;
  stage: JobStage;
  rep_id: string | null;
  /** A `date` column. Compared as a string, never parsed — see `bookedDate`. */
  work_date: string | null;
  /** A practice job. Never in the email, as it is never on the payout board. */
  is_test?: boolean | null;
  orders: readonly ReminderOrder[];
}

export interface ReminderPerson {
  id: string;
  full_name: string | null;
}

export interface ReminderOrderLine {
  id: string;
  orderNumber: number;
  crew: string;
  state: PayoutState;
  amount: Cents;
  reason: ReminderReason | null;
  /** Null unless on hold; then the reason it was parked, "" if none. */
  hold: string | null;
}

export interface ReminderJobLine {
  id: string;
  /** "#1042 — Jane Smith — 1428 Elm St", the way a person says which job. */
  label: string;
  stage: ReminderStage;
  orders: ReminderOrderLine[];
  /** Whether any of its work orders is waiting on somebody. */
  flagged: boolean;
}

export interface ReminderBucket {
  stage: ReminderStage;
  title: string;
  jobs: ReminderJobLine[];
}

export interface ReminderGroup {
  repId: string | null;
  repName: string;
  buckets: ReminderBucket[];
  /** How many of this rep's jobs are waiting on somebody. */
  flagged: number;
  jobs: number;
}

export interface ReminderPayment {
  jobLabel: string;
  orderNumber: number;
  crew: string;
  amount: Cents;
}

export interface PayoutReminder {
  groups: ReminderGroup[];
  counts: Record<ReminderStage, number>;
  openJobs: number;
  /** Jobs — not work orders — with at least one thing waiting on somebody. */
  flagged: number;
  /** Approved and not yet paid: the list Kelsey works from today. */
  toPay: ReminderPayment[];
  toPayTotal: Cents;
}

/**
 * The calendar date in Birmingham, from an instant.
 *
 * Through `Intl` rather than by slicing the ISO string: a crew booked for 7pm
 * on Monday is stored as Tuesday 00:00 UTC, and slicing would call Monday's
 * work overdue a day early — every week, for every evening booking.
 */
export function centralDate(at: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/**
 * The last day the crew was expected, as a plain calendar date.
 *
 * The work order's own booking first — its end, or its start when it has no
 * end — and the job's `work_date` when it has none. A three-day tear-off
 * booked Monday to Wednesday is not overdue on Tuesday (ADR 0177). `work_date` is a `date` column, which is already a calendar date and
 * must never be handed to `new Date` — that reads it as UTC midnight and shows
 * the day before to anybody west of Greenwich.
 */
export function bookedDate(
  job: Pick<ReminderJob, "work_date">,
  order: Pick<ReminderOrder, "start_at" | "end_at">,
): string | null {
  const booked = order.end_at || order.start_at;
  if (booked) return centralDate(new Date(booked));
  const date = (job.work_date ?? "").trim();
  return date === "" ? null : date.slice(0, 10);
}

/**
 * The one thing waiting to be done about this work order, or nothing.
 *
 * Order matters: the reasons are read most-finished first, so an approved
 * payout reads as Kelsey's to pay rather than as a booking that went by. A
 * rejected payout is deliberately silent — Lysa sent it back on purpose, and
 * the crew's note is on the board where she left it. One on hold has nothing
 * to tick either — it is waiting on a W-9 or a bank account — so it has no
 * reason here, and its line says "on hold" with the hold's reason instead
 * (`holdText`, ADR 0315).
 */
export function orderReason(
  job: Pick<ReminderJob, "stage" | "work_date">,
  order: ReminderOrder,
  today: string,
): ReminderReason | null {
  // Our own employees are on payroll. Their work is costed, never paid out.
  if (!isPayable(order)) return null;

  const state = payoutState(order);
  if (state === "needs_review") return "awaiting_approval";
  if (state === "approved") return "awaiting_payment";
  // paid, sent, rejected, held — settled, or parked on purpose. A held one is
  // still named on its line, as on hold (ADR 0315).
  if (state !== "not_ready") return null;

  // Nobody ticked it, and it is on an estimate the job has since moved off.
  // That work is not going to happen under this order, so it is nobody's to
  // chase. A finished order on an old estimate is still owed, and is caught
  // above exactly as the payout board shows it (ADR 0172).
  if (order.is_live === false) return null;

  const booked = bookedDate(job, order);
  if (booked !== null && booked < today) return "booked_day_passed";
  // No booking at all, but the job says the work happened. This is the case a
  // date-based check cannot see, and the one that sits unpaid longest.
  if (job.stage === "complete") return "job_complete_not_ticked";
  return null;
}

const jobLabel = (
  job: Pick<ReminderJob, "job_number" | "customer_name" | "address">,
): string =>
  [
    job.job_number ? `#${job.job_number}` : null,
    job.customer_name?.trim() || null,
    job.address,
  ]
    .filter(Boolean)
    .join(" — ") || "a job";

const isReminderStage = (stage: JobStage): stage is ReminderStage =>
  (REMINDER_STAGES as readonly string[]).includes(stage);

function orderLine(
  job: ReminderJob,
  order: ReminderOrder,
  today: string,
): ReminderOrderLine {
  return {
    id: order.id,
    orderNumber: order.order_number,
    crew: crewLabel(order.crew) ?? "no crew on the order",
    state: payoutState(order),
    amount: payoutTotal(order),
    reason: orderReason(job, order, today),
    hold:
      payoutState(order) === "held" ? (order.payout_note ?? "").trim() : null,
  };
}

/**
 * Everything Thursday morning's email says, from the open jobs.
 *
 * `today` is passed in rather than read from a clock so this stays pure and so
 * the caller decides whose calendar it is — the cron fires at 7am Birmingham
 * time, which is a different date from UTC's for five hours of every day.
 */
export function buildPayoutReminder(input: {
  jobs: readonly ReminderJob[];
  /** Staff, in the order their groups should appear. */
  people: readonly ReminderPerson[];
  today: string;
}): PayoutReminder {
  const { jobs, people, today } = input;

  // A practice job is nobody's money. The function also leaves them out of its
  // query; this is here so the rule holds whoever calls it.
  const open = jobs.filter(
    (job) => isReminderStage(job.stage) && job.is_test !== true,
  );

  const counts: Record<ReminderStage, number> = {
    signed: 0,
    in_production: 0,
    complete: 0,
  };
  const toPay: ReminderPayment[] = [];

  const lines = new Map<string | null, ReminderJobLine[]>();
  let flagged = 0;

  for (const job of open) {
    const stage = job.stage as ReminderStage;
    counts[stage] += 1;

    // In-house crews are dropped here rather than hidden in the email: their
    // work is never paid out, so a line about it is a line nobody can act on.
    const orders = job.orders
      .filter(isPayable)
      .map((order) => orderLine(job, order, today))
      .sort((a, b) => a.orderNumber - b.orderNumber);

    const label = jobLabel(job);
    const line: ReminderJobLine = {
      id: job.id,
      label,
      stage,
      orders,
      flagged: orders.some((order) => order.reason !== null),
    };
    if (line.flagged) flagged += 1;

    for (const order of orders) {
      if (order.reason === "awaiting_payment") {
        toPay.push({
          jobLabel: label,
          orderNumber: order.orderNumber,
          crew: order.crew,
          amount: order.amount,
        });
      }
    }

    const key = job.rep_id ?? null;
    const bucket = lines.get(key);
    if (bucket) bucket.push(line);
    else lines.set(key, [line]);
  }

  const groupFor = (
    repId: string | null,
    repName: string,
  ): ReminderGroup | null => {
    const jobLines = lines.get(repId);
    if (!jobLines || jobLines.length === 0) return null;
    return {
      repId,
      repName,
      buckets: REMINDER_STAGES.map((stage) => ({
        stage,
        title: REMINDER_STAGE_TITLE[stage],
        // Something waiting on somebody goes first: a rep who reads only the
        // top of their section has still read the part that needed them.
        jobs: jobLines
          .filter((line) => line.stage === stage)
          .sort((a, b) =>
            a.flagged === b.flagged
              ? a.label.localeCompare(b.label)
              : a.flagged
                ? -1
                : 1,
          ),
      })).filter((bucket) => bucket.jobs.length > 0),
      flagged: jobLines.filter((line) => line.flagged).length,
      jobs: jobLines.length,
    };
  };

  const groups: ReminderGroup[] = [];
  for (const person of people) {
    const group = groupFor(person.id, person.full_name?.trim() || "A rep");
    if (group) groups.push(group);
  }
  // Anybody holding jobs who is not in `people` — a deactivated rep, most
  // likely. Their jobs still exist and still need ticking, so they are not
  // dropped; they are simply last.
  for (const repId of lines.keys()) {
    if (repId === null || groups.some((group) => group.repId === repId)) {
      continue;
    }
    const group = groupFor(repId, "A rep who has left");
    if (group) groups.push(group);
  }
  const unassigned = groupFor(null, NO_REP);
  if (unassigned) groups.push(unassigned);

  return {
    groups,
    counts,
    openJobs: open.length,
    flagged,
    toPay: toPay.sort((a, b) => a.orderNumber - b.orderNumber),
    toPayTotal: addCents(...toPay.map((payment) => payment.amount)),
  };
}

export interface ReminderEmail {
  subject: string;
  text: string;
  html: string;
}

/** The three steps the email exists to remind people of. */
export const REMINDER_PROCESS: readonly string[] = [
  "Lysa marks the work order complete.",
  "Lysa marks it approved.",
  "Kelsey pays the crew from Mercury and logs it in QuickBooks.",
];

const plural = (count: number, one: string, many = `${one}s`): string =>
  `${count} ${count === 1 ? one : many}`;

/** "Thursday 18 September", the way the email dates itself. */
function longDate(today: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date(`${today}T00:00:00Z`));
}

/**
 * The letter itself, in both parts.
 *
 * It is sent on a quiet week too, saying so. A reminder that arrives only when
 * something is wrong cannot be told apart from a reminder that has stopped
 * working, and the second is the failure this whole thing exists to prevent.
 */
export function reminderEmail(
  report: PayoutReminder,
  options: { url: string; today: string },
): ReminderEmail {
  const { url, today } = options;

  const subject =
    report.flagged === 0
      ? `Thursday check — ${plural(report.openJobs, "open job")}, nothing to chase`
      : `Thursday check — ${plural(report.flagged, "job needs", "jobs need")} a tick`;

  const summary =
    `${report.counts.signed} signed, ` +
    `${report.counts.in_production} scheduled or ongoing, ` +
    `${report.counts.complete} completed.`;

  const text: string[] = [
    `Every open job, ${longDate(today)}.`,
    "",
    summary,
    report.flagged === 0
      ? "Nothing is waiting on anybody this week."
      : `${plural(report.flagged, "job is", "jobs are")} waiting on somebody — marked ⚠ below.`,
    "",
    "The process, when a crew has finished:",
    ...REMINDER_PROCESS.map((step, index) => `  ${index + 1}. ${step}`),
    "",
  ];

  for (const group of report.groups) {
    text.push(
      `── ${group.repName} — ${plural(group.jobs, "job")}${
        group.flagged > 0 ? `, ${group.flagged} needing a tick` : ""
      }`,
    );
    for (const bucket of group.buckets) {
      text.push(`   ${bucket.title} (${bucket.jobs.length})`);
      for (const job of bucket.jobs) {
        text.push(`     ${job.flagged ? "⚠ " : "  "}${job.label}`);
        for (const order of job.orders) {
          const reason = order.reason
            ? ` — ${REMINDER_REASON_TEXT[order.reason]}`
            : order.hold !== null
              ? ` — ${holdText(order.hold)}`
              : "";
          text.push(
            `         WO-${order.orderNumber} · ${order.crew} · ${formatMoney(
              order.amount,
            )}${reason}`,
          );
        }
        if (job.orders.length === 0) {
          text.push("         no crew work order yet");
        }
      }
    }
    text.push("");
  }

  text.push(
    report.toPay.length === 0
      ? "Nothing is approved and waiting to be paid today."
      : `── To pay today — ${formatMoney(report.toPayTotal)} across ${plural(
          report.toPay.length,
          "work order",
        )}`,
  );
  for (const payment of report.toPay) {
    text.push(
      `     WO-${payment.orderNumber} · ${payment.crew} · ${formatMoney(
        payment.amount,
      )} · ${payment.jobLabel}`,
    );
  }

  text.push("", `Open the payout board: ${url}`);

  return {
    subject,
    text: text.join("\n"),
    html: reminderHtml(report, options),
  };
}

const escape = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * The same letter as a page.
 *
 * Inline styles only, and no image, table layout or web font: this is read in
 * Gmail on a phone, and everything clever about an HTML email is something one
 * client strips. The plain-text part above is the real letter; this is it with
 * the warnings made findable.
 */
function reminderHtml(
  report: PayoutReminder,
  options: { url: string; today: string },
): string {
  const out: string[] = [];
  const p = (body: string, style = "") =>
    out.push(`<p style="margin:0 0 10px${style}">${body}</p>`);

  out.push(
    '<div style="font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;' +
      'font-size:14px;line-height:1.5;color:#1a1a1a;max-width:720px">',
  );

  p(`<b>Every open job, ${escape(longDate(options.today))}.</b>`);
  p(
    escape(
      `${report.counts.signed} signed, ` +
        `${report.counts.in_production} scheduled or ongoing, ` +
        `${report.counts.complete} completed.`,
    ),
  );
  p(
    report.flagged === 0
      ? "Nothing is waiting on anybody this week."
      : `<b>${escape(
          plural(report.flagged, "job is", "jobs are"),
        )} waiting on somebody</b>, marked below.`,
  );

  out.push(
    '<p style="margin:0 0 4px">The process, when a crew has finished:</p>',
    '<ol style="margin:0 0 18px;padding-left:20px">',
    ...REMINDER_PROCESS.map((step) => `<li>${escape(step)}</li>`),
    "</ol>",
  );

  for (const group of report.groups) {
    out.push(
      '<h3 style="margin:22px 0 2px;font-size:15px;border-bottom:1px solid #d8d8d8;' +
        `padding-bottom:4px">${escape(group.repName)}</h3>`,
      '<p style="margin:0 0 10px;color:#666;font-size:13px">' +
        escape(
          `${plural(group.jobs, "job")}${
            group.flagged > 0 ? `, ${group.flagged} needing a tick` : ""
          }`,
        ) +
        "</p>",
    );

    for (const bucket of group.buckets) {
      out.push(
        '<p style="margin:12px 0 4px;font-weight:600;color:#444">' +
          `${escape(bucket.title)} (${bucket.jobs.length})</p>`,
      );
      for (const job of bucket.jobs) {
        const border = job.flagged ? "#c8791a" : "#e2e2e2";
        out.push(
          `<div style="border-left:3px solid ${border};padding:2px 0 2px 10px;margin:0 0 8px">`,
          `<div style="font-weight:${job.flagged ? 600 : 400}">${
            job.flagged ? "&#9888; " : ""
          }${escape(job.label)}</div>`,
        );
        for (const order of job.orders) {
          const reason = order.reason
            ? ` — <b>${escape(REMINDER_REASON_TEXT[order.reason])}</b>`
            : order.hold !== null
              ? ` — ${escape(holdText(order.hold))}`
              : "";
          out.push(
            '<div style="color:#555;font-size:13px">' +
              `WO-${order.orderNumber} &middot; ${escape(order.crew)} &middot; ` +
              `${escape(formatMoney(order.amount))}${reason}</div>`,
          );
        }
        if (job.orders.length === 0) {
          out.push(
            '<div style="color:#555;font-size:13px">no crew work order yet</div>',
          );
        }
        out.push("</div>");
      }
    }
  }

  out.push(
    '<h3 style="margin:24px 0 6px;font-size:15px;border-bottom:1px solid #d8d8d8;' +
      'padding-bottom:4px">To pay today</h3>',
  );
  if (report.toPay.length === 0) {
    p("Nothing is approved and waiting to be paid.");
  } else {
    p(
      `<b>${escape(formatMoney(report.toPayTotal))}</b> across ${escape(
        plural(report.toPay.length, "work order"),
      )}.`,
    );
    out.push('<div style="color:#555;font-size:13px">');
    for (const payment of report.toPay) {
      out.push(
        `<div>WO-${payment.orderNumber} &middot; ${escape(
          payment.crew,
        )} &middot; ${escape(formatMoney(payment.amount))} &middot; ${escape(
          payment.jobLabel,
        )}</div>`,
      );
    }
    out.push("</div>");
  }

  out.push(
    `<p style="margin:20px 0 0"><a href="${escape(options.url)}">` +
      "Open the payout board in Roof Board</a></p>",
    "</div>",
  );

  return out.join("\n");
}
